import { authService } from './auth.service';

/**
 * Single sign-on handoff from the external college site / training app.
 *
 * The source authenticates the user and redirects into this portal as
 * `/<path>?token=<jwt>` (e.g. `/?token=...`). We adopt that JWT as the current
 * access token *before* React mounts and `AuthProvider` runs its `initAuth` —
 * otherwise the guard would bounce to the sign-in page before the token is
 * stored — then strip the token from the URL so it doesn't linger in history.
 *
 * The handoff carries the access token, and optionally a refresh token. With a
 * refresh token the session auto-renews on 401 like a normal login; without it
 * the session lasts only until the access token expires (then the user is sent
 * back to sign-in). Must be awaited once, before `createRoot().render()`.
 */
export const consumeSsoToken = async (): Promise<void> => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  if (!token) return;

  // Optional refresh token — support either casing the source might use.
  const refreshToken =
    params.get('refreshToken') ?? params.get('refresh_token') ?? '';

  // Adopt the URL token as the current session, overriding any existing one, so
  // AuthProvider hydrates from it. saveLoginData decodes the JWT; a malformed
  // token just logs and leaves storage without user info, and validateToken()
  // in AuthProvider will then route an invalid/expired token back to sign-in.
  await authService.saveLoginData(token, refreshToken);

  // Remove the tokens from the URL regardless of outcome, so they can't be
  // re-read, bookmarked, or shared. Keep the path and any other params.
  params.delete('token');
  params.delete('refreshToken');
  params.delete('refresh_token');
  const query = params.toString();
  const cleanUrl =
    window.location.pathname +
    (query ? `?${query}` : '') +
    window.location.hash;
  window.history.replaceState(window.history.state, '', cleanUrl);
};
